import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { BarChart3, PieChart as PieChartIcon, TrendingUp, TrendingDown, Hash, Layers, Download, ScatterChart as ScatterIcon, Activity, Filter } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line, ScatterChart, Scatter, ZAxis } from 'recharts';
import { useTheme } from '../context/ThemeContext';
import { useTimezone } from '../context/TimezoneContext';
import { formatDateInTimezone, formatDateOnly, formatTimeOnly } from '../utils/dateUtils';
import { SkeletonStats } from './Skeleton';
import submissionAnalyticsService from '../api/services/submissionAnalyticsService';
import { formSchemaService } from '../api';

const CHART_COLORS = ['#59caee', '#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4'];
const STATUS_COLORS = { Draft: '#94a3b8', InProgress: '#3b82f6', Submitted: '#59caee', UnderReview: '#f59e0b', Approved: '#22c55e', Rejected: '#ef4444', Archived: '#6b7280' };

// ── Styled Components ───────────────────────────────────────────────────────

const PanelContainer = styled.div`
  overflow: hidden;
  max-height: ${p => p.$isOpen ? '1400px' : '0'};
  opacity: ${p => p.$isOpen ? 1 : 0};
  transition: max-height 0.4s ease, opacity 0.25s ease;
  margin-bottom: ${p => p.$isOpen ? p.theme.spacing.lg : '0'};
`;
const PanelCard = styled.div`
  background: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  overflow: hidden;
`;
const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  padding: 0 ${p => p.theme.spacing.md};
  overflow-x: auto;
`;
const Tab = styled.button`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.lg};
  font-size: 13px;
  font-weight: ${p => p.$active ? '600' : '500'};
  color: ${p => p.$active ? p.theme.colors.tint : p.theme.colors.muted};
  background: none; border: none;
  border-bottom: 2px solid ${p => p.$active ? p.theme.colors.tint : 'transparent'};
  cursor: pointer; display: flex; align-items: center; gap: 6px; white-space: nowrap;
  transition: all 0.15s;
  &:hover { color: ${p => p.theme.colors.tint}; }
`;
const TabContent = styled.div`padding: ${p => p.theme.spacing.lg};`;

// Overview
const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.lg};
`;
const MiniStat = styled.div`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
`;
const MiniStatLabel = styled.div`font-size: 12px; font-weight: 600; color: ${p => p.theme.colors.muted}; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const MiniStatValue = styled.div`font-size: 20px; font-weight: 700; color: ${p => p.theme.colors.text}; line-height: 1.2;`;
const MiniStatRange = styled.div`font-size: 11px; color: ${p => p.theme.colors.muted}; margin-top: 2px;`;
const TrendBadge = styled.span`
  display: inline-flex; align-items: center; gap: 2px;
  font-size: 11px; font-weight: 600; padding: 1px 6px; border-radius: 10px; margin-left: 6px;
  background: ${p => p.$positive ? '#22c55e20' : '#ef444420'};
  color: ${p => p.$positive ? '#22c55e' : '#ef4444'};
`;
const SectionLabel = styled.h4`
  font-size: 12px; font-weight: 600; color: ${p => p.theme.colors.muted};
  text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 ${p => p.theme.spacing.sm} 0;
`;

// Completeness
const CompBar = styled.div`display: flex; align-items: center; gap: ${p => p.theme.spacing.sm}; margin-bottom: 6px;`;
const CompLabel = styled.span`font-size: 12px; color: ${p => p.theme.colors.text}; min-width: 110px; max-width: 140px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const CompTrack = styled.div`flex: 1; height: 16px; background: ${p => p.theme.colors.border}; border-radius: 8px; overflow: hidden;`;
const CompFill = styled.div`height: 100%; width: ${p => p.$pct}%; border-radius: 8px;
  background: ${p => p.$pct > 80 ? '#22c55e' : p.$pct > 50 ? '#f59e0b' : '#ef4444'};
  transition: width 0.3s ease; min-width: ${p => p.$pct > 0 ? '2px' : '0'};`;
const CompPct = styled.span`font-size: 11px; color: ${p => p.theme.colors.muted}; min-width: 38px; text-align: right;`;

// Chart Builder
const ControlsRow = styled.div`display: flex; flex-wrap: wrap; gap: ${p => p.theme.spacing.sm}; margin-bottom: ${p => p.theme.spacing.md}; align-items: flex-end;`;
const ControlGroup = styled.div`display: flex; flex-direction: column; gap: 4px;`;
const ControlLabel = styled.label`font-size: 11px; font-weight: 600; color: ${p => p.theme.colors.muted}; text-transform: uppercase; letter-spacing: 0.04em;`;
const ControlSelect = styled.select`
  padding: 6px 10px; font-size: 13px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text}; cursor: pointer; min-width: 130px;
  &:focus { outline: none; border-color: ${p => p.theme.colors.tint}; }
`;
const ChartToggle = styled.div`display: flex; border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md}; overflow: hidden; margin-left: auto;`;
const ChartToggleBtn = styled.button`
  padding: 6px 10px; font-size: 12px; border: none;
  background: ${p => p.$active ? p.theme.colors.tint + '18' : 'transparent'};
  color: ${p => p.$active ? p.theme.colors.tint : p.theme.colors.muted};
  cursor: pointer; display: flex; align-items: center; gap: 3px; transition: all 0.15s;
  &:hover { background: ${p => p.theme.colors.tint}10; }
  &:not(:last-child) { border-right: 1px solid ${p => p.theme.colors.border}; }
`;
const ChartArea = styled.div`min-height: 280px; display: flex; align-items: center; justify-content: center; position: relative;`;
const ChartHint = styled.div`text-align: center; color: ${p => p.theme.colors.muted}; font-size: 13px; padding: ${p => p.theme.spacing.xl};`;

// Export
const ExportBtn = styled.button`
  position: absolute; top: 4px; right: 4px; z-index: 10;
  padding: 4px 8px; font-size: 11px; display: flex; align-items: center; gap: 4px;
  border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.theme.colors.backgroundSecondary}; color: ${p => p.theme.colors.muted};
  cursor: pointer; transition: all 0.15s;
  &:hover { border-color: ${p => p.theme.colors.tint}; color: ${p => p.theme.colors.tint}; }
`;

// Dist
const DistGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: ${p => p.theme.spacing.lg};`;
const DistCard = styled.div`border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md}; padding: ${p => p.theme.spacing.md}; background: ${p => p.theme.colors.background};`;
const DistHeader = styled.div`display: flex; align-items: center; justify-content: space-between; margin-bottom: ${p => p.theme.spacing.sm};`;
const DistTitle = styled.span`font-size: 13px; font-weight: 600; color: ${p => p.theme.colors.text};`;
const DistBadge = styled.span`font-size: 11px; color: ${p => p.theme.colors.tint}; background: ${p => p.theme.colors.tint}12; padding: 2px 8px; border-radius: ${p => p.theme.borderRadius.sm};`;
const EmptyMsg = styled.div`text-align: center; padding: ${p => p.theme.spacing.xl}; color: ${p => p.theme.colors.muted}; font-size: 13px;`;

// ── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (v) => { if (v == null) return '-'; const n = Number(v); if (Number.isInteger(n)) return n.toLocaleString(); return n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 3 }); };
const FUNCTIONS = [{ value: 'avg', label: 'Average' }, { value: 'sum', label: 'Sum' }, { value: 'count', label: 'Count' }, { value: 'min', label: 'Min' }, { value: 'max', label: 'Max' }];

const downloadCSV = (data, filename) => {
  if (!data?.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(','), ...data.map(row => keys.map(k => `"${row[k] ?? ''}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const downloadSVG = (chartRef, filename) => {
  const svg = chartRef?.current?.querySelector?.('svg');
  if (!svg) return;
  const data = new XMLSerializer().serializeToString(svg);
  const blob = new Blob([data], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ── Component ───────────────────────────────────────────────────────────────

export const AnalyticsPanel = ({ isOpen, aggregationData, isLoading, schemaFields = [], formSchemaId, fieldFilters = [], filterLogic = 'and', showInactive = false }) => {
  const theme = useTheme();
  const { timezoneId } = useTimezone();
  const chartRef = useRef(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Summary data (for status pie + completeness)
  const [summaryData, setSummaryData] = useState(null);

  // Timeline data
  const [timelineData, setTimelineData] = useState(null);

  // Trend data (this month vs last month)
  const [trendData, setTrendData] = useState(null);

  // Chart Builder state
  const [metricField, setMetricField] = useState('');
  const [aggFunction, setAggFunction] = useState('avg');
  const [groupByField, setGroupByField] = useState('');
  const [chartType, setChartType] = useState('bar');
  const [groupByData, setGroupByData] = useState(null);
  const [groupByLoading, setGroupByLoading] = useState(false);
  const [dateGrouping, setDateGrouping] = useState('month');

  // Scatter state
  const [scatterX, setScatterX] = useState('');
  const [scatterY, setScatterY] = useState('');
  const [scatterData, setScatterData] = useState(null);
  const [scatterLoading, setScatterLoading] = useState(false);

  // ── Repeated-form child schema loading (mirrors FilterPanel pattern) ─────
  // Allows the Metric dropdown to surface numeric fields that live inside a
  // child (repeated-form) schema. Composite keys (`rf:{schemaId}:{fieldKey}`)
  // disambiguate parent vs child fields and are parsed before the API call.
  const [childSchemas, setChildSchemas] = useState({}); // { [schemaId]: { name, fields: [...] } }
  const [childSchemasLoaded, setChildSchemasLoaded] = useState(new Set());

  const repeatedFormFields = useMemo(
    () => schemaFields.filter(f => {
      const t = typeof f.type === 'number' ? f.type : -1;
      return (t === 73 || String(f.type) === 'RepeatedForm') && f.configuration?.repeatedFormSchemaId;
    }),
    [schemaFields]
  );

  useEffect(() => {
    if (!isOpen) return;
    const toFetch = repeatedFormFields.filter(rf => !childSchemasLoaded.has(rf.configuration.repeatedFormSchemaId));
    if (toFetch.length === 0) return;

    setChildSchemasLoaded(prev => {
      const next = new Set(prev);
      toFetch.forEach(rf => next.add(rf.configuration.repeatedFormSchemaId));
      return next;
    });

    toFetch.forEach(async (rfField) => {
      try {
        const schema = await formSchemaService.getFormSchemaById(rfField.configuration.repeatedFormSchemaId);
        let definition = schema?.schemaDefinition || schema;
        if (typeof definition === 'string') definition = JSON.parse(definition);
        let fieldsArray = definition?.fields;
        if (fieldsArray && !Array.isArray(fieldsArray) && fieldsArray.$values) fieldsArray = fieldsArray.$values;

        if (Array.isArray(fieldsArray)) {
          const childFields = fieldsArray
            .filter(f => f.key)
            .map(f => ({
              key: f.key,
              title: f.title || f.key,
              type: f.type,
              configuration: f.config || f.configuration || {},
            }));
          setChildSchemas(prev => ({
            ...prev,
            [rfField.configuration.repeatedFormSchemaId]: {
              name: schema?.name || rfField.title || 'Repeated Form',
              parentFieldKey: rfField.key,
              fields: childFields,
            }
          }));
        }
      } catch (err) {
        console.error(`Failed to fetch child schema for ${rfField.key}:`, err);
      }
    });
  }, [isOpen, repeatedFormFields, childSchemasLoaded]);

  const isNumericType = (t) => {
    const tn = typeof t === 'number' ? t : -1;
    return [10, 11, 12, 71, 72].includes(tn) || ['Number', 'Currency', 'Percentage', 'Rating', 'Slider'].includes(String(t));
  };

  // Field lists. Numeric fields include parent fields plus repeated-form children
  // (children carry composite keys `rf:{schemaId}:{fieldKey}` so the dropdown can
  // round-trip them through state without ambiguity).
  const numericFields = useMemo(() => {
    const parents = schemaFields
      .filter(f => isNumericType(f.type))
      .map(f => ({ key: f.key, title: f.title || f.key, repeatedFormSchemaId: null, parentLabel: null }));

    const children = [];
    for (const rfField of repeatedFormFields) {
      const schemaId = rfField.configuration.repeatedFormSchemaId;
      const childData = childSchemas[schemaId];
      if (!childData?.fields?.length) continue;
      for (const cf of childData.fields) {
        if (isNumericType(cf.type)) {
          children.push({
            key: `rf:${schemaId}:${cf.key}`,
            title: cf.title || cf.key,
            repeatedFormSchemaId: schemaId,
            parentLabel: childData.name,
          });
        }
      }
    }

    return [...parents, ...children];
  }, [schemaFields, repeatedFormFields, childSchemas]);

  const categoricalFields = schemaFields.filter(f => {
    const t = typeof f.type === 'number' ? f.type : -1;
    return [0, 1, 20, 21, 24].includes(t) || ['Text', 'TextArea', 'Select', 'Radio', 'Toggle'].includes(String(f.type));
  });
  const dateFields = schemaFields.filter(f => {
    const t = typeof f.type === 'number' ? f.type : -1;
    return [30, 31, 32].includes(t) || ['Date', 'Time', 'DateTime'].includes(String(f.type));
  });
  const groupByOptions = [
    { key: '__createdAt', title: 'Submission Date', isDate: true },
    ...categoricalFields.map(f => ({ key: f.key, title: f.title || f.key, isDate: false })),
    ...dateFields.map(f => ({ key: f.key, title: f.title || f.key, isDate: true })),
  ];
  const selectedGroupByIsDate = groupByOptions.find(f => f.key === groupByField)?.isDate || false;

  const formatGroupLabel = useCallback((groupValue) => {
    if (!groupValue || !groupValue.includes('T')) return groupValue;
    return formatDateInTimezone(groupValue, timezoneId, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  }, [timezoneId]);

  // Auto-select
  useEffect(() => {
    if (numericFields.length > 0 && !metricField) setMetricField(numericFields[0].key);
    if (numericFields.length >= 1 && !scatterX) setScatterX(numericFields[0].key);
    if (!scatterY) {
      if (numericFields.length > 1) setScatterY(numericFields[1].key);
      else if (dateFields.length > 0) setScatterY(dateFields[0].key);
    }
    if (groupByOptions.length > 0 && !groupByField) setGroupByField(groupByOptions[0].key);
  }, [schemaFields]);

  // Fetch summary (status + completeness — form-wide, not filtered)
  useEffect(() => {
    if (!isOpen || !formSchemaId) return;
    submissionAnalyticsService.getSummary(formSchemaId).then(setSummaryData).catch(() => {});
  }, [isOpen, formSchemaId]);

  // Fetch timeline (respects active filters)
  useEffect(() => {
    if (!isOpen || !formSchemaId) return;
    submissionAnalyticsService.getGroupBy(formSchemaId, {
      groupByFieldKey: '__createdAt', function: 'count', dateGrouping: 'month',
      filters: fieldFilters.length > 0 ? fieldFilters : undefined,
      filterLogic: fieldFilters.length > 0 ? filterLogic : undefined,
      includeInactive: showInactive,
      userTimezone: timezoneId,
    }).then(setTimelineData).catch(() => {});
  }, [isOpen, formSchemaId, fieldFilters, filterLogic, showInactive, timezoneId]);

  // Fetch trend — this month vs last month (respects active filters)
  useEffect(() => {
    if (!isOpen || !formSchemaId) return;
    const now = new Date();
    const thisMonthFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const lastMonthFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
    const lastMonthTo = new Date(now.getFullYear(), now.getMonth(), 0).toISOString();
    const filterParams = fieldFilters.length > 0
      ? { filters: fieldFilters, filterLogic }
      : {};
    Promise.all([
      submissionAnalyticsService.getAggregation(formSchemaId, { fromDate: thisMonthFrom, includeInactive: showInactive, ...filterParams }),
      submissionAnalyticsService.getAggregation(formSchemaId, { fromDate: lastMonthFrom, toDate: lastMonthTo, includeInactive: showInactive, ...filterParams }),
    ]).then(([thisMonth, lastMonth]) => {
      const trends = {};
      (thisMonth?.fields || []).forEach(f => {
        const last = (lastMonth?.fields || []).find(lf => lf.fieldKey === f.fieldKey);
        if (last?.mean && last.mean !== 0) {
          trends[f.fieldKey] = Math.round(((f.mean - last.mean) / Math.abs(last.mean)) * 100);
        }
      });
      setTrendData(trends);
    }).catch(() => {});
  }, [isOpen, formSchemaId, fieldFilters, filterLogic, showInactive]);

  // Parse a composite metric key. Returns the raw field key plus an optional
  // repeated-form schema ID extracted from the `rf:{schemaId}:{fieldKey}` prefix.
  const parseMetricKey = (rawKey) => {
    if (!rawKey || !rawKey.startsWith('rf:')) return { fieldKey: rawKey, repeatedFormSchemaId: null };
    const parts = rawKey.split(':');
    const schemaId = parseInt(parts[1], 10);
    return {
      fieldKey: parts.slice(2).join(':'),
      repeatedFormSchemaId: Number.isFinite(schemaId) ? schemaId : null,
    };
  };

  // Fetch GroupBy
  const fetchGroupBy = useCallback(async () => {
    if (!formSchemaId || !groupByField) return;
    if (aggFunction !== 'count' && !metricField) return;
    setGroupByLoading(true);
    try {
      const { fieldKey: aggregateFieldKey, repeatedFormSchemaId: aggregateRepeatedFormSchemaId } = parseMetricKey(metricField);
      const data = await submissionAnalyticsService.getGroupBy(formSchemaId, {
        groupByFieldKey: groupByField,
        aggregateFieldKey: aggFunction !== 'count' ? aggregateFieldKey : undefined,
        aggregateRepeatedFormSchemaId: aggFunction !== 'count' ? aggregateRepeatedFormSchemaId ?? undefined : undefined,
        function: aggFunction,
        dateGrouping: selectedGroupByIsDate ? dateGrouping : undefined,
        filters: fieldFilters.length > 0 ? fieldFilters : undefined,
        filterLogic: fieldFilters.length > 0 ? filterLogic : undefined,
        includeInactive: showInactive,
        userTimezone: timezoneId,
      });
      setGroupByData(data);
    } catch (err) { console.error('GroupBy fetch failed:', err); setGroupByData(null); }
    finally { setGroupByLoading(false); }
  }, [formSchemaId, groupByField, metricField, aggFunction, dateGrouping, selectedGroupByIsDate, fieldFilters, filterLogic, showInactive, timezoneId]);

  useEffect(() => { if (isOpen && activeTab === 'chart' && chartType !== 'scatter') fetchGroupBy(); }, [isOpen, activeTab, chartType, fetchGroupBy]);

  // Fetch Scatter
  const fetchScatter = useCallback(async () => {
    if (!formSchemaId || !scatterX || !scatterY) return;
    setScatterLoading(true);
    try {
      const data = await submissionAnalyticsService.getScatter(formSchemaId, {
        xFieldKey: scatterX, yFieldKey: scatterY, maxPoints: 500,
        filters: fieldFilters.length > 0 ? fieldFilters : undefined,
        filterLogic: fieldFilters.length > 0 ? filterLogic : undefined,
        includeInactive: showInactive,
      });
      setScatterData(data);
    } catch { setScatterData(null); }
    finally { setScatterLoading(false); }
  }, [formSchemaId, scatterX, scatterY, fieldFilters, filterLogic, showInactive]);

  useEffect(() => { if (isOpen && activeTab === 'chart' && chartType === 'scatter') fetchScatter(); }, [isOpen, activeTab, chartType, fetchScatter]);

  if (!isOpen) return <PanelContainer $isOpen={false} />;
  if (isLoading) return <PanelContainer $isOpen><PanelCard><TabContent><SkeletonStats /></TabContent></PanelCard></PanelContainer>;

  const { fields = [], distributions = [], submissionCount = 0 } = aggregationData || {};
  const hasNoSubmissions = submissionCount === 0 && !isLoading;
  const hasNoNumericFields = numericFields.length === 0;
  const statusData = summaryData?.statusBreakdown ? Object.entries(summaryData.statusBreakdown).map(([k, v]) => ({ name: k, value: v })) : [];
  const completenessFields = (summaryData?.fields || []).filter(f => f.fieldType !== 'Divider' && f.fieldType !== 'Header' && f.fieldType !== 'Spacer').sort((a, b) => a.completenessPercent - b.completenessPercent);

  return (
    <PanelContainer $isOpen>
      <PanelCard>
        <TabBar>
          <Tab $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}><Activity size={14} /> Overview</Tab>
          <Tab $active={activeTab === 'chart'} onClick={() => setActiveTab('chart')}><BarChart3 size={14} /> Chart Builder</Tab>
          <Tab $active={activeTab === 'distributions'} onClick={() => setActiveTab('distributions')}><Layers size={14} /> Distributions</Tab>
        </TabBar>
        <TabContent>

          {/* ═══ OVERVIEW ═══ */}
          {activeTab === 'overview' && (<>
            {fieldFilters.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', marginBottom: theme.spacing.sm,
                background: theme.colors.tint + '10', border: `1px solid ${theme.colors.tint}25`,
                borderRadius: theme.borderRadius.md, fontSize: '12px', color: theme.colors.tint, fontWeight: 600,
              }}>
                <Filter size={12} /> Showing filtered results ({fieldFilters.length} {fieldFilters.length === 1 ? 'filter' : 'filters'} applied)
              </div>
            )}
            {/* Empty state */}
            {hasNoSubmissions && (
              <EmptyMsg>
                <Hash size={32} style={{ opacity: 0.3, marginBottom: '8px' }} /><br />
                No submissions yet. Analytics will appear once forms are submitted.
              </EmptyMsg>
            )}
            {/* Stat cards with trends */}
            {!hasNoSubmissions && fields.length > 0 && (<>
              <SectionLabel>Numeric Summary ({submissionCount} submissions)</SectionLabel>
              <StatsRow>
                {fields.map(f => {
                  const trend = trendData?.[f.fieldKey];
                  return (
                    <MiniStat key={f.fieldKey}>
                      <MiniStatLabel title={f.fieldTitle}>
                        {f.fieldTitle || f.fieldKey}
                        {trend != null && trend !== 0 && (
                          <TrendBadge $positive={trend > 0}>
                            {trend > 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                            {Math.abs(trend)}%
                          </TrendBadge>
                        )}
                      </MiniStatLabel>
                      <MiniStatValue>{fmt(f.mean)}</MiniStatValue>
                      <MiniStatRange>{fmt(f.min)} – {fmt(f.max)} ({f.count} values)</MiniStatRange>
                    </MiniStat>
                  );
                })}
              </StatsRow>
            </>)}

            {/* Timeline + Status Pie */}
            <OverviewGrid>
              <div>
                <SectionLabel>Submission Timeline</SectionLabel>
                {timelineData?.buckets?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={timelineData.buckets.map(b => ({ name: formatGroupLabel(b.groupValue), count: b.count }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                      <XAxis dataKey="name" stroke={theme.colors.muted} style={{ fontSize: '11px' }} />
                      <YAxis stroke={theme.colors.muted} style={{ fontSize: '11px' }} />
                      <Tooltip contentStyle={{ backgroundColor: theme.colors.backgroundSecondary, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md, color: theme.colors.text, fontSize: '12px' }} />
                      <Line type="monotone" dataKey="count" stroke={theme.colors.tint} strokeWidth={2} dot={{ fill: theme.colors.tint, r: 3 }} name="Submissions" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyMsg>No timeline data available.</EmptyMsg>}
              </div>
              <div>
                <SectionLabel>Status Breakdown</SectionLabel>
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={statusData} cx="50%" cy="50%" innerRadius={45} outerRadius={80} dataKey="value" nameKey="name" paddingAngle={2}>
                        {statusData.map((entry, i) => <Cell key={i} fill={STATUS_COLORS[entry.name] || CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: theme.colors.backgroundSecondary, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md, color: theme.colors.text, fontSize: '12px' }} />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <EmptyMsg>No status data.</EmptyMsg>}
              </div>
            </OverviewGrid>

            {/* Field Completeness */}
            {completenessFields.length > 0 && (<>
              <SectionLabel>Field Completeness</SectionLabel>
              {completenessFields.map(f => (
                <CompBar key={f.fieldKey}>
                  <CompLabel title={f.fieldTitle}>{f.fieldTitle || f.fieldKey}</CompLabel>
                  <CompTrack><CompFill $pct={f.completenessPercent} /></CompTrack>
                  <CompPct>{Math.round(f.completenessPercent)}%</CompPct>
                </CompBar>
              ))}
            </>)}
          </>)}

          {/* ═══ CHART BUILDER ═══ */}
          {activeTab === 'chart' && (<>
            {hasNoNumericFields && aggFunction !== 'count' && (
              <div style={{
                padding: '8px 12px', marginBottom: theme.spacing.sm,
                background: theme.colors.warning + '12', border: `1px solid ${theme.colors.warning || '#f59e0b'}30`,
                borderRadius: theme.borderRadius.md, fontSize: '12px', color: theme.colors.muted,
              }}>
                This form has no numeric fields. Use the <strong>Count</strong> function to chart submission counts by category.
              </div>
            )}
            <ControlsRow>
              {chartType !== 'scatter' ? (<>
                <ControlGroup>
                  <ControlLabel>Metric</ControlLabel>
                  <ControlSelect value={metricField} onChange={e => setMetricField(e.target.value)} disabled={aggFunction === 'count' || hasNoNumericFields}>
                    {hasNoNumericFields && <option value="">No numeric fields</option>}
                    {/* Parent numeric fields render ungrouped at the top */}
                    {numericFields.filter(f => !f.repeatedFormSchemaId).map(f => (
                      <option key={f.key} value={f.key}>{f.title}</option>
                    ))}
                    {/* Repeated-form children grouped per child schema */}
                    {Object.entries(
                      numericFields.filter(f => f.repeatedFormSchemaId).reduce((acc, f) => {
                        if (!acc[f.repeatedFormSchemaId]) acc[f.repeatedFormSchemaId] = { name: f.parentLabel, items: [] };
                        acc[f.repeatedFormSchemaId].items.push(f);
                        return acc;
                      }, {})
                    ).map(([sid, group]) => (
                      <optgroup key={sid} label={group.name}>
                        {group.items.map(f => <option key={f.key} value={f.key}>{f.title}</option>)}
                      </optgroup>
                    ))}
                  </ControlSelect>
                </ControlGroup>
                <ControlGroup>
                  <ControlLabel>Function</ControlLabel>
                  <ControlSelect value={aggFunction} onChange={e => setAggFunction(e.target.value)}>
                    {FUNCTIONS.map(fn => <option key={fn.value} value={fn.value}>{fn.label}</option>)}
                  </ControlSelect>
                </ControlGroup>
                <ControlGroup>
                  <ControlLabel>Group By</ControlLabel>
                  <ControlSelect value={groupByField} onChange={e => setGroupByField(e.target.value)}>
                    <optgroup label="Submission">
                      <option value="__createdAt">Submission Date</option>
                    </optgroup>
                    {categoricalFields.length > 0 && <optgroup label="Fields">{categoricalFields.map(f => <option key={f.key} value={f.key}>{f.title || f.key}</option>)}</optgroup>}
                    {dateFields.length > 0 && <optgroup label="Date Fields">{dateFields.map(f => <option key={f.key} value={f.key}>{f.title || f.key}</option>)}</optgroup>}
                  </ControlSelect>
                </ControlGroup>
                {selectedGroupByIsDate && (
                  <ControlGroup>
                    <ControlLabel>Period</ControlLabel>
                    <ControlSelect value={dateGrouping} onChange={e => setDateGrouping(e.target.value)}>
                      <option value="minute">Minute</option><option value="hour">Hour</option><option value="day">Day</option><option value="week">Week</option><option value="month">Month</option><option value="year">Year</option>
                    </ControlSelect>
                  </ControlGroup>
                )}
              </>) : (<>
                <ControlGroup>
                  <ControlLabel>X Axis</ControlLabel>
                  <ControlSelect value={scatterX} onChange={e => setScatterX(e.target.value)}>
                    {numericFields.length > 0 && <optgroup label="Numeric">{numericFields.map(f => <option key={f.key} value={f.key}>{f.title || f.key}</option>)}</optgroup>}
                    {dateFields.length > 0 && <optgroup label="Date / Time">{dateFields.map(f => <option key={f.key} value={f.key}>{f.title || f.key}</option>)}</optgroup>}
                  </ControlSelect>
                </ControlGroup>
                <ControlGroup>
                  <ControlLabel>Y Axis</ControlLabel>
                  <ControlSelect value={scatterY} onChange={e => setScatterY(e.target.value)}>
                    {numericFields.length > 0 && <optgroup label="Numeric">{numericFields.map(f => <option key={f.key} value={f.key}>{f.title || f.key}</option>)}</optgroup>}
                    {dateFields.length > 0 && <optgroup label="Date / Time">{dateFields.map(f => <option key={f.key} value={f.key}>{f.title || f.key}</option>)}</optgroup>}
                  </ControlSelect>
                </ControlGroup>
              </>)}
              <ChartToggle>
                <ChartToggleBtn $active={chartType === 'bar'} onClick={() => setChartType('bar')}><BarChart3 size={13} /> Bar</ChartToggleBtn>
                <ChartToggleBtn $active={chartType === 'pie'} onClick={() => setChartType('pie')}><PieChartIcon size={13} /> Pie</ChartToggleBtn>
                {(numericFields.length >= 2 || (numericFields.length >= 1 && dateFields.length >= 1)) && <ChartToggleBtn $active={chartType === 'scatter'} onClick={() => setChartType('scatter')}><ScatterIcon size={13} /> Scatter</ChartToggleBtn>}
              </ChartToggle>
            </ControlsRow>

            <ChartArea ref={chartRef}>
              {/* Export button */}
              {((chartType !== 'scatter' && groupByData?.buckets?.length > 0) || (chartType === 'scatter' && scatterData?.points?.length > 0)) && (
                <ExportBtn onClick={() => {
                  if (chartType === 'scatter') {
                    const xIsDate = ['Date', 'DateTime', 'Time'].includes(scatterData?.xFieldType);
                    const yIsDate = ['Date', 'DateTime', 'Time'].includes(scatterData?.yFieldType);
                    downloadCSV(scatterData.points.map(p => ({
                      x: xIsDate ? new Date(p.xTimestamp).toISOString() : p.x,
                      y: yIsDate ? new Date(p.yTimestamp).toISOString() : p.y,
                    })), 'scatter.csv');
                  }
                  else downloadCSV(groupByData.buckets.map(b => ({ group: formatGroupLabel(b.groupValue), value: b.aggregateValue, count: b.count })), 'chart.csv');
                }}><Download size={12} /> CSV</ExportBtn>
              )}

              {chartType === 'scatter' ? (
                scatterLoading ? <ChartHint>Loading scatter data...</ChartHint> :
                !scatterData?.points?.length ? <ChartHint>No data. Select a numeric field and optionally a date field.</ChartHint> :
                (() => {
                  const DATE_TYPES = ['Date', 'DateTime', 'Time'];
                  const xIsDate = DATE_TYPES.includes(scatterData?.xFieldType);
                  const yIsDate = DATE_TYPES.includes(scatterData?.yFieldType);
                  // Pick the right formatter for each axis: time-only for Time, date-only for Date,
                  // date+time for DateTime. Fixes TASK-0187 where toLocaleDateString stripped time.
                  // Date/Time/DateTime field values are naive wall-clock (no timezone). The API
                  // encodes them as UTC-based epoch millis, so they must be read back in UTC —
                  // applying the user's timezone here shifts them (TASK-0214: 8 AM showed as 2 AM).
                  const fmtFor = (fieldType) => {
                    if (fieldType === 'Time') return ts => ts ? formatTimeOnly(new Date(ts).toISOString(), 'UTC') : '';
                    if (fieldType === 'Date') return ts => ts ? formatDateOnly(new Date(ts).toISOString(), timezoneId) : '';
                    return ts => ts ? formatDateInTimezone(new Date(ts).toISOString(), 'UTC', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : '';
                  };
                  const fmtX = fmtFor(scatterData?.xFieldType);
                  const fmtY = fmtFor(scatterData?.yFieldType);
                  const normalizedPoints = scatterData.points.map(p => ({
                    x: xIsDate ? (p.xTimestamp ?? 0) : p.x,
                    y: yIsDate ? (p.yTimestamp ?? 0) : p.y,
                  }));
                  return (
                    <ResponsiveContainer width="100%" height={300}>
                      <ScatterChart>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                        <XAxis type="number" dataKey="x" name={scatterData.xFieldTitle || scatterX} stroke={theme.colors.muted} style={{ fontSize: '11px' }} tickFormatter={xIsDate ? fmtX : undefined} domain={xIsDate ? ['auto', 'auto'] : undefined} />
                        <YAxis type="number" dataKey="y" name={scatterData.yFieldTitle || scatterY} stroke={theme.colors.muted} style={{ fontSize: '11px' }} tickFormatter={yIsDate ? fmtY : undefined} domain={yIsDate ? ['auto', 'auto'] : undefined} />
                        <ZAxis range={[40, 40]} />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={{ backgroundColor: theme.colors.backgroundSecondary, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md, color: theme.colors.text, fontSize: '12px' }} formatter={(value, name) => [xIsDate && name === (scatterData.xFieldTitle || scatterX) ? fmtX(value) : yIsDate && name === (scatterData.yFieldTitle || scatterY) ? fmtY(value) : value, name]} />
                        <Scatter data={normalizedPoints} fill={theme.colors.tint} fillOpacity={0.7} />
                      </ScatterChart>
                    </ResponsiveContainer>
                  );
                })()
              ) : groupByLoading ? <ChartHint>Loading chart data...</ChartHint> :
                !groupByData?.buckets?.length ? <ChartHint>No data available. Try different selections.</ChartHint> :
                chartType === 'bar' ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={groupByData.buckets.map(b => ({ name: formatGroupLabel(b.groupValue), value: Number(b.aggregateValue) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                      <XAxis dataKey="name" stroke={theme.colors.muted} style={{ fontSize: '11px' }} />
                      <YAxis stroke={theme.colors.muted} style={{ fontSize: '11px' }} />
                      <Tooltip contentStyle={{ backgroundColor: theme.colors.backgroundSecondary, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md, color: theme.colors.text }} formatter={(v) => [fmt(v), FUNCTIONS.find(fn => fn.value === aggFunction)?.label || 'Value']} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>{groupByData.buckets.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={groupByData.buckets.map(b => ({ name: formatGroupLabel(b.groupValue), value: Number(b.aggregateValue) }))} cx="50%" cy="50%" innerRadius={60} outerRadius={110} dataKey="value" nameKey="name" paddingAngle={2} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                        {groupByData.buckets.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: theme.colors.backgroundSecondary, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md, color: theme.colors.text }} formatter={(v) => [fmt(v), FUNCTIONS.find(fn => fn.value === aggFunction)?.label || 'Value']} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                )
              }
            </ChartArea>
          </>)}

          {/* ═══ DISTRIBUTIONS ═══ */}
          {activeTab === 'distributions' && (
            distributions.length > 0 ? (
              <DistGrid>
                {distributions.map(dist => (
                  <DistCard key={dist.fieldKey}>
                    <DistHeader>
                      <DistTitle>{dist.fieldTitle || dist.fieldKey}</DistTitle>
                      <DistBadge>{dist.uniqueCount} unique</DistBadge>
                    </DistHeader>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={dist.topValues.slice(0, 8).map(tv => ({ name: tv.value, value: tv.count }))} layout="vertical" margin={{ left: 80, right: 16, top: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} horizontal={false} />
                        <XAxis type="number" stroke={theme.colors.muted} style={{ fontSize: '11px' }} />
                        <YAxis type="category" dataKey="name" stroke={theme.colors.muted} style={{ fontSize: '11px' }} width={75} />
                        <Tooltip contentStyle={{ backgroundColor: theme.colors.backgroundSecondary, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md, color: theme.colors.text, fontSize: '12px' }} />
                        <Bar dataKey="value" fill={theme.colors.tint} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </DistCard>
                ))}
              </DistGrid>
            ) : <EmptyMsg>No categorical fields with value distributions.</EmptyMsg>
          )}

        </TabContent>
      </PanelCard>
    </PanelContainer>
  );
};

export default AnalyticsPanel;
